import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await signIn(formData.email, formData.password);
    
    if (error) {
      toast.error(error);
    } else {
      toast.success('Welcome back!');
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-20 flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-[#737373] hover:text-[#0A0A0A] mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <div className="mb-8">
            <h1 className="font-serif text-3xl font-semibold text-[#0A0A0A]">Welcome Back</h1>
            <p className="text-[#737373] mt-2">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm text-[#737373]">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
                className="mt-2 h-12 rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-[#0A0A0A]"
                data-testid="login-email"
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-sm text-[#737373]">Password</Label>
                <Link to="/forgot-password" className="text-sm text-[#C9A962] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-2">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Enter your password"
                  className="h-12 rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-[#0A0A0A] pr-12"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#0A0A0A]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none btn-lift"
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#737373]">
            Don't have an account?{' '}
            <Link to="/signup" className="text-[#0A0A0A] font-medium hover:text-[#C9A962]">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Right - Branding */}
      <div className="hidden lg:flex w-1/2 bg-[#0A0A0A] items-center justify-center p-16">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A962]/30 text-[#C9A962] text-xs uppercase tracking-widest mb-8">
            <Sparkles className="w-3 h-3" />
            Premium Access
          </div>
          <h2 className="font-serif text-4xl font-semibold text-white leading-tight">
            Elevate Your
            <span className="block text-[#C9A962]">Corporate Identity</span>
          </h2>
          <p className="mt-6 text-[#A3A3A3]">
            Access exclusive products, track your orders, and manage your corporate branding needs all in one place.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
